import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import type {Member} from '@/entities/member/types';
import {
  fetchUserPolicies,
  grantUserPermission,
  revokeUserPermission,
  togglePermission,
} from '@/entities/permission/api';
import {
  EFFECT_ALLOW,
  EFFECT_DENY,
  effectOf,
  PERM_ACTIONS,
  PERM_RESOURCES,
  type PolicyRule,
} from '@/entities/permission/types';
import {fetchUserRoles, revokeRolesInCompany, rolesInCompany} from '@/entities/role/api';
import {useCompanyRoles} from '@/features/manage-members/lib/company-roles';
import {ApiError} from '@/shared/api';
import {Badge} from '@/shared/ui/badge';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {ChoiceChips, Segmented} from '@/shared/ui/segmented';
import {SectionHeader} from '@/shared/ui/section';
import {Sheet} from '@/shared/ui/sheet';
import {Skeleton} from '@/shared/ui/skeleton';
import {AppText} from '@/shared/ui/text';
import {spacing} from '@/shared/ui/theme';

/**
 * Everything one member may do in this company, and the three levers that change it.
 *
 * ## Why a role chip was not enough
 *
 * A role is a bundle somebody else decided the contents of. Real companies keep
 * arriving at "the accountant, but also the door log" — and until this sheet existed
 * the only answers were to invent a role on the platform or to make the person an
 * administrator. Casbin has always taken a rule written straight against an account
 * (`POST /permissions/user/assign`), which is exactly that missing middle.
 *
 * ## What is shown, and what cannot be
 *
 * The list is the member's DIRECT rules only. `GET /permissions/user/{id}` reads rows
 * whose subject is the account; a role's rules are written against the ROLE CODE and
 * no route anywhere reads them back. So the sheet says «сверх роли» and means it — an
 * empty list is a member with no personal exceptions, never a member with no access.
 *
 * ## The two traps this file exists to not fall into
 *
 * `effect` arrives as a string (`"allow"`) and is written as an integer (`0`). Toggle
 * takes `current_effect`, and a wrong value there does not fail — the remove half
 * matches nothing, the add half runs anyway, and the rule ends up stored under BOTH
 * effects. Every effect here goes through `effectOf` on the row that was actually
 * read; none is typed in.
 *
 * `GET /roles/users/{id}` is not scoped to the caller's company at all — it answers
 * with every assignment the account holds anywhere. `rolesInCompany` filters before
 * anything is drawn, because a member card that lists somebody's role at their other
 * employer is a leak.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(4), paddingBottom: spacing(2)},
  block: {gap: spacing(2)},
  card: {gap: spacing(3)},
  chips: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(2)},
  rule: {flexDirection: 'row', alignItems: 'center', gap: spacing(2)},
  ruleText: {flex: 1},
  row: {flexDirection: 'row', gap: spacing(2), flexWrap: 'wrap'},
  grow: {flexGrow: 1, flexBasis: 130},
});

/** `devices` → «Устройства», falling back to the raw object name for anything new. */
function useResourceLabel() {
  const {t} = useTranslation();
  return (resource: string) => t(`perm.resource.${resource}`, resource);
}

function useActionLabel() {
  const {t} = useTranslation();
  return (action: string) => t(`perm.action.${action}`, action);
}

function PolicyRow({
  rule,
  onToggle,
  onRevoke,
  busy,
}: {
  rule: PolicyRule;
  onToggle: () => void;
  onRevoke: () => void;
  busy: boolean;
}) {
  const {t} = useTranslation();
  const resourceLabel = useResourceLabel();
  const actionLabel = useActionLabel();
  const deny = effectOf(rule.effect) === EFFECT_DENY;

  return (
    <View style={styles.rule}>
      <View style={styles.ruleText}>
        <AppText variant="body">{resourceLabel(rule.object)}</AppText>
        <AppText variant="caption">{actionLabel(rule.action)}</AppText>
      </View>
      <Badge
        variant={deny ? 'bad' : 'good'}
        size="sm"
        label={t(deny ? 'perm.effect.deny' : 'perm.effect.allow')}
      />
      <AppButton
        title={t(deny ? 'perm.makeAllow' : 'perm.makeDeny')}
        tone="ghost"
        size="sm"
        disabled={busy}
        onPress={onToggle}
      />
      <AppButton
        title={t('perm.remove')}
        tone="ghost"
        size="sm"
        disabled={busy}
        onPress={onRevoke}
      />
    </View>
  );
}

export function MemberAccessSheet({
  companyId,
  member,
  visible,
  onClose,
}: {
  companyId: string;
  member: Member | null;
  visible: boolean;
  onClose: () => void;
}) {
  const {t} = useTranslation();
  const qc = useQueryClient();
  const roles = useCompanyRoles();
  const resourceLabel = useResourceLabel();
  const actionLabel = useActionLabel();

  const [resource, setResource] = useState<string>(PERM_RESOURCES[0]);
  const [action, setAction] = useState<string>('read');
  const [effect, setEffect] = useState<'allow' | 'deny'>('allow');
  const [confirmWipe, setConfirmWipe] = useState(false);

  const accountId = member?.account_id ?? '';
  const enabled = visible && !!accountId;

  const assignments = useQuery({
    queryKey: ['member-roles', companyId, accountId],
    queryFn: () => fetchUserRoles(companyId, accountId),
    enabled,
  });

  const policies = useQuery({
    queryKey: ['member-policies', companyId, accountId],
    queryFn: () => fetchUserPolicies(companyId, accountId),
    enabled,
  });

  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({queryKey: ['member-roles', companyId, accountId]}),
      qc.invalidateQueries({queryKey: ['member-policies', companyId, accountId]}),
      qc.invalidateQueries({queryKey: ['members', companyId]}),
    ]);
  };

  const grant = useMutation({
    meta: {toast: 'toast.accessSaved'},
    mutationFn: () =>
      grantUserPermission(companyId, {
        userId: accountId,
        object: resource,
        action,
        effect: effect === 'deny' ? EFFECT_DENY : EFFECT_ALLOW,
      }),
    onSuccess: refresh,
  });

  const revoke = useMutation({
    meta: {toast: 'toast.accessSaved'},
    mutationFn: (rule: PolicyRule) =>
      revokeUserPermission(companyId, {
        userId: accountId,
        object: rule.object,
        action: rule.action,
        // Part of the key, not a hint: revoking under the wrong effect removes nothing.
        effect: effectOf(rule.effect),
      }),
    onSuccess: refresh,
  });

  const flip = useMutation({
    meta: {toast: 'toast.accessSaved'},
    mutationFn: (rule: PolicyRule) =>
      togglePermission(companyId, {
        subject: accountId,
        object: rule.object,
        action: rule.action,
        currentEffect: effectOf(rule.effect),
      }),
    onSuccess: refresh,
  });

  const wipeRoles = useMutation({
    mutationFn: () => revokeRolesInCompany(companyId, accountId),
    onSuccess: async () => {
      setConfirmWipe(false);
      await refresh();
    },
  });

  const errorOf = (e: unknown): string =>
    e instanceof ApiError ? e.message : t('errors.generic');

  const held = rolesInCompany(assignments.data ?? [], companyId);
  const rules = policies.data ?? [];
  const busy = grant.isPending || revoke.isPending || flip.isPending;
  const name = member?.full_name || member?.email || member?.username || '';

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      title={t('memberAccess.title')}
      subtitle={name}
      testID="member-access">
      <View style={styles.root}>
        {/* ── Roles held here ─────────────────────────────────────────────── */}
        <View style={styles.block}>
          <SectionHeader
            title={t('memberAccess.roles')}
            hint={t('memberAccess.rolesHint')}
          />
          <Card padding={4} style={styles.card}>
            {assignments.isPending ? (
              <Skeleton height={28} />
            ) : assignments.isError ? (
              <Banner tone="error" title={errorOf(assignments.error)} />
            ) : held.length === 0 ? (
              <AppText variant="caption">{t('memberAccess.noRoles')}</AppText>
            ) : (
              <View style={styles.chips}>
                {held.map(a => (
                  <Badge
                    key={a.id || a.role_code}
                    variant="secondary"
                    size="sm"
                    label={roles.labelFor(a.role_code)}
                  />
                ))}
              </View>
            )}

            {/* revoke-scope, not DELETE /roles/users/{id}: the latter erases this
                person's roles in every company they belong to and is refused to
                anyone but a platform administrator anyway. */}
            {held.length > 0 ? (
              confirmWipe ? (
                <>
                  <Banner tone="warning" title={t('memberAccess.wipeConfirm')} />
                  {wipeRoles.isError ? (
                    <Banner tone="error" title={errorOf(wipeRoles.error)} />
                  ) : null}
                  <View style={styles.row}>
                    <AppButton
                      title={t('memberAccess.wipeDo')}
                      tone="destructive"
                      size="sm"
                      loading={wipeRoles.isPending}
                      onPress={() => wipeRoles.mutate()}
                      style={styles.grow}
                      testID="member-wipe-roles-confirm"
                    />
                    <AppButton
                      title={t('actions.cancel')}
                      tone="ghost"
                      size="sm"
                      onPress={() => setConfirmWipe(false)}
                      style={styles.grow}
                    />
                  </View>
                </>
              ) : (
                <AppButton
                  title={t('memberAccess.wipe')}
                  tone="neutral"
                  size="sm"
                  onPress={() => setConfirmWipe(true)}
                  testID="member-wipe-roles"
                />
              )
            ) : null}

            {/* "0" is the honest answer when they held nothing here, and it is
                otherwise indistinguishable from the call having done something. */}
            {wipeRoles.isSuccess ? (
              <Banner
                tone="success"
                title={t('memberAccess.wiped', {
                  count: wipeRoles.data?.revoked_count ?? 0,
                })}
              />
            ) : null}
          </Card>
        </View>

        {/* ── Direct rules ────────────────────────────────────────────────── */}
        <View style={styles.block}>
          <SectionHeader
            title={t('memberAccess.direct')}
            hint={t('memberAccess.directHint')}
          />
          <Card padding={4} style={styles.card}>
            {policies.isPending ? (
              <Skeleton height={40} />
            ) : policies.isError ? (
              <Banner tone="error" title={errorOf(policies.error)} />
            ) : rules.length === 0 ? (
              <AppText variant="caption">{t('memberAccess.noDirect')}</AppText>
            ) : (
              rules.map(rule => (
                <PolicyRow
                  key={`${rule.object}:${rule.action}:${rule.effect}`}
                  rule={rule}
                  busy={busy}
                  onToggle={() => flip.mutate(rule)}
                  onRevoke={() => revoke.mutate(rule)}
                />
              ))
            )}
            {revoke.isError ? (
              <Banner tone="error" title={errorOf(revoke.error)} />
            ) : null}
            {flip.isError ? <Banner tone="error" title={errorOf(flip.error)} /> : null}
          </Card>
        </View>

        {/* ── Add one ─────────────────────────────────────────────────────── */}
        <View style={styles.block}>
          <SectionHeader title={t('memberAccess.add')} />
          <Card padding={4} style={styles.card}>
            <AppText variant="label">{t('memberAccess.resource')}</AppText>
            <ChoiceChips
              value={resource}
              onChange={setResource}
              testID="perm-resource"
              options={PERM_RESOURCES.map(r => ({
                value: r,
                label: resourceLabel(r),
              }))}
            />

            <AppText variant="label">{t('memberAccess.action')}</AppText>
            <ChoiceChips
              value={action}
              onChange={setAction}
              testID="perm-action"
              options={PERM_ACTIONS.map(a => ({value: a, label: actionLabel(a)}))}
            />

            <Segmented
              value={effect}
              onChange={setEffect}
              testID="perm-effect"
              options={[
                {value: 'allow', label: t('perm.effect.allow')},
                {value: 'deny', label: t('perm.effect.deny')},
              ]}
            />

            {grant.isError ? (
              <Banner tone="error" title={errorOf(grant.error)} />
            ) : null}
            <AppButton
              title={t('memberAccess.grant')}
              tone="soft"
              size="sm"
              loading={grant.isPending}
              onPress={() => grant.mutate()}
              testID="perm-grant"
            />
          </Card>
        </View>
      </View>
    </Sheet>
  );
}
